import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { NewsletterSource } from '@wanderlust/shared';
import { NewsletterForm } from './NewsletterForm';
import { LegalLinks } from '../legal/LegalLinks';
import { SOCIAL_PROFILES, type SocialNetwork } from '../../lib/socialProfiles';

interface BlogFooterProps {
  /**
   * Set false on pages that already render their own signup (blog posts) so two
   * identical panels don't stack.
   */
  showNewsletter?: boolean;
  /**
   * Set false on pages that end with their own CTA section (the marketing
   * page), where a second planning button reads as a stutter.
   */
  showCta?: boolean;
  /** Placement reported to analytics for the footer's own signup. */
  newsletterSource?: NewsletterSource;
}

/**
 * Footer links sit a shade brighter than the /60 body text around them, and
 * hold their name on one line the way the registration number below does.
 */
const LINK_CLASS = 'text-white/80 hover:text-white hover:underline whitespace-nowrap';

const SOCIAL_LINK_CLASS =
  'inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-white/80 hover:text-white hover:border-white/50 hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-accent';

/** lucide's outline glyphs, drawn here because lucide ships no brand marks. */
const SOCIAL_GLYPHS: Record<SocialNetwork, ReactNode> = {
  facebook: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
  instagram: (
    <>
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </>
  ),
};

export function BlogFooter({
  showNewsletter = true,
  showCta = true,
  newsletterSource = 'footer',
}: BlogFooterProps = {}) {
  return (
    <footer className="bg-primary text-white/60 py-12 mt-auto">
      <div className="max-w-5xl mx-auto px-6 flex flex-col items-center gap-8">
        {showNewsletter && <NewsletterForm variant="dark" source={newsletterSource} />}
        {showCta && (
          <Link
            to="/plan"
            className="px-6 py-2.5 bg-accent hover:bg-accent-hover text-primary font-medium rounded transition-colors"
          >
            Start Planning Your Next Trip
          </Link>
        )}
        {/* Plain anchors for the same reason as the WanderNerd line below. */}
        <div className="flex items-center gap-3">
          {SOCIAL_PROFILES.map((profile) => (
            <a
              key={profile.network}
              href={profile.url}
              aria-label={`Wanderlust Cravings on ${profile.label}`}
              title={profile.label}
              className={SOCIAL_LINK_CLASS}
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {SOCIAL_GLYPHS[profile.network]}
              </svg>
            </a>
          ))}
        </div>
        <div className="flex flex-col items-center gap-2">
          {/* WanderNerd is the same author's site of data-driven travel tools.
              Plain anchors so they're in the HTML the server sends, which is
              also what the prerender pass writes for the marketing pages. */}
          <p className="text-sm text-center">
            Tools by the same author:{' '}
            <a href="https://wandernerd.com/park-drops" className={LINK_CLASS}>
              Park Drops
            </a>
            ,{' '}
            <a href="https://wandernerd.com/cruise-crowds" className={LINK_CLASS}>
              Cruise Crowds
            </a>
            , and more at{' '}
            <a href="https://wandernerd.com" className={LINK_CLASS}>
              WanderNerd
            </a>
            .
          </p>
          {/* Washington requires registered sellers of travel to publish the
              registration number wherever they advertise. JSX drops the
              whitespace between these spans, so as one line it has nowhere to
              break and runs off a phone screen; below `sm` each part takes its
              own line instead, which also keeps a bullet from dangling at the
              end of one. */}
          <p className="text-sm text-center">
            <span className="block sm:inline">
              &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> Wanderlust Cravings
            </span>
            <span className="hidden sm:inline mx-2" aria-hidden="true">
              &bull;
            </span>
            <span className="block sm:inline whitespace-nowrap">WA Seller of Travel #606135323</span>
          </p>
          <LegalLinks className="text-sm" linkClassName={LINK_CLASS} />
        </div>
      </div>
    </footer>
  );
}
